import { useQuery } from '@tanstack/react-query';
import { ExternalLink, FileText } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Skeleton } from '@/design/components';
import type { Lesson } from './api';
import { SlidesViewer } from './SlidesViewer';

/**
 * גוף השיעור לפי סוג התוכן.
 *
 * הסוגים מגיעים מטבלה (lesson_content_types), ולכן אין כאן רשימה
 * קשיחה אלא מיפוי לפי renderer_key, עם נפילה לטקסט כשמוסיפים סוג
 * חדש שהממשק עוד לא מכיר. ככה הוספת סוג לא שוברת מסך.
 */
export function LessonBody({ lesson }: { lesson: Lesson }) {
  const media = useSignedUrl(lesson.media_path);

  return (
    <div className="grid gap-3">
      {lesson.content_type === 'link' && lesson.external_url && (
        <a
          href={lesson.external_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-fit items-center gap-2 text-body font-medium text-ink"
        >
          <ExternalLink size={20} strokeWidth={1.75} aria-hidden="true" />
          לפתיחת החומר בלשונית חדשה
        </a>
      )}

      {lesson.media_path && media.isPending && <Skeleton className="h-48" />}

      {media.data && lesson.content_type === 'image' && (
        <img src={media.data} alt={lesson.title} className="w-full rounded-md" />
      )}

      {media.data && lesson.content_type === 'video' && (
        // controls ולא autoplay: העובד בסניף, אולי בלי אוזניות
        <video src={media.data} controls className="w-full rounded-md" />
      )}

      {media.data && lesson.content_type === 'slides' && (
        <SlidesViewer key={lesson.id} url={media.data} title={lesson.title} />
      )}

      {media.data && lesson.content_type === 'pdf' && (
        <a
          href={media.data}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-fit items-center gap-2 text-body font-medium text-ink"
        >
          <FileText size={20} strokeWidth={1.75} aria-hidden="true" />
          לפתיחת ה PDF
        </a>
      )}

      {/* הטקסט הוא תוכן שההנהלה הקלידה, ולכן מוצג כטקסט ולא כ HTML.
          whitespace-pre-line שומר על שבירות השורה שהם כתבו. */}
      {lesson.body && (
        <p className="m-0 whitespace-pre-line text-body leading-relaxed">{lesson.body}</p>
      )}
    </div>
  );
}

/**
 * כתובת חתומה לקובץ בבדל learning. הבדל פרטי, ולכן אין URL ציבורי.
 * שעה תוקף: יותר מדי לשיעור אחד, ופחות מדי בשביל לדאוג.
 */
function useSignedUrl(path: string | null) {
  return useQuery({
    queryKey: ['learning-media', path],
    enabled: Boolean(path),
    staleTime: 50 * 60 * 1000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.storage
        .from('learning')
        .createSignedUrl(path as string, 3600);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
  });
}
